"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api.mjs";
import AuthScreen from "./AuthScreen";
import CalculatorPage from "./CalculatorPage";
import RoomsPage from "./RoomsPage";
import ReservationsPage from "./ReservationsPage";
import AvailabilityPage from "./AvailabilityPage";
import AdminPage from "./AdminPage";
import { Icon } from "./Icons";

const NAV_ITEMS = [
  { id: "calculator", label: "Calculator", icon: "calculator" },
  { id: "rooms", label: "Rooms", icon: "rooms" },
  { id: "reservations", label: "Reservations", icon: "calendar" },
  { id: "availability", label: "Availability", icon: "availability" },
  { id: "admin", label: "Admin", icon: "settings" }
];

export default function HotelApp() {
  const [booting, setBooting] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [user, setUser] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [activePage, setActivePage] = useState("calculator");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [toast, setToast] = useState(null);

  const notify = useCallback((message, tone = "success") => {
    setToast({ message, tone, id: Date.now() });
  }, []);

  const refresh = useCallback(async () => {
    const data = await api.snapshot();
    setSnapshot(data);
    return data;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const status = await api.status();
        if (!alive) return;
        setNeedsSetup(status.needsSetup);
        if (!status.needsSetup) {
          const session = await api.me();
          if (session.user) {
            setUser(session.user);
            const data = await api.snapshot();
            if (alive) setSnapshot(data);
          }
        }
      } catch (error) {
        if (alive) setAuthError(error.message);
      } finally {
        if (alive) setBooting(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  async function handleAuth(form) {
    setAuthLoading(true);
    setAuthError("");
    try {
      const result = needsSetup
        ? await api.bootstrap(form)
        : await api.login(form);
      setUser(result.user);
      setNeedsSetup(false);
      await refresh();
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthLoading(false);
    }
  }

  async function logout() {
    await api.logout();
    setUser(null);
    setSnapshot(null);
    setActivePage("calculator");
  }

  const actions = useMemo(
    () => ({
      async create(resource, value, message = "Saved") {
        const result = await api.create(resource, value);
        await refresh();
        notify(message);
        return result;
      },
      async update(resource, id, value, message = "Changes saved") {
        const result = await api.update(resource, id, value);
        await refresh();
        notify(message);
        return result;
      },
      async remove(resource, id, message = "Deleted") {
        await api.remove(resource, id);
        await refresh();
        notify(message);
      },
      async updateSettings(value) {
        await api.updateSettings(value);
        await refresh();
        notify("Pricing settings saved");
      },
      async exportData() {
        const data = await api.exportData();
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: "application/json"
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `felix-rate-desk-${new Date().toISOString().slice(0, 10)}.json`;
        anchor.click();
        URL.revokeObjectURL(url);
        notify("Backup downloaded");
      },
      notify,
      refresh,
      navigate: setActivePage
    }),
    [notify, refresh]
  );

  if (booting) {
    return (
      <main className="loading-screen">
        <div className="brand-mark brand-mark-large">F</div>
        <span className="loading-dot" />
        <p>Preparing your rate desk…</p>
      </main>
    );
  }

  if (!user || !snapshot) {
    return (
      <AuthScreen
        error={authError}
        loading={authLoading}
        needsSetup={needsSetup}
        onSubmit={handleAuth}
      />
    );
  }

  const pageProps = { data: snapshot, actions, user };
  const pages = {
    calculator: <CalculatorPage {...pageProps} />,
    rooms: <RoomsPage {...pageProps} />,
    reservations: <ReservationsPage {...pageProps} />,
    availability: <AvailabilityPage {...pageProps} />,
    admin: <AdminPage {...pageProps} />
  };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark">F</div>
          <div>
            <strong>Felix Rate Desk</strong>
            <span>{snapshot.settings.propertyName}</span>
          </div>
        </div>
        <nav className="sidebar-nav" aria-label="Main navigation">
          {NAV_ITEMS.map((item) => (
            <button
              className={activePage === item.id ? "active" : ""}
              key={item.id}
              onClick={() => setActivePage(item.id)}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="mode-badge">
            <Icon name={snapshot.mode === "cloud" ? "cloud" : "device"} />
            <div>
              <strong>
                {snapshot.mode === "cloud" ? "Cloud sync" : "This device"}
              </strong>
              <span>
                {snapshot.mode === "cloud"
                  ? "D1 database connected"
                  : "Offline data mode"}
              </span>
            </div>
          </div>
          <div className="profile-chip">
            <span className="profile-avatar">
              {user.name?.slice(0, 1).toUpperCase()}
            </span>
            <div>
              <strong>{user.name}</strong>
              <span>{user.role === "admin" ? "Administrator" : "Staff"}</span>
            </div>
            <button onClick={logout} aria-label="Sign out">
              <Icon name="logout" size={18} />
            </button>
          </div>
        </div>
      </aside>

      <main className="app-main">{pages[activePage]}</main>

      <nav className="mobile-nav" aria-label="Main navigation">
        {NAV_ITEMS.map((item) => (
          <button
            className={activePage === item.id ? "active" : ""}
            key={item.id}
            onClick={() => setActivePage(item.id)}
          >
            <Icon name={item.icon} size={21} />
            <span>{item.label === "Reservations" ? "Bookings" : item.label}</span>
          </button>
        ))}
      </nav>

      {toast ? (
        <div className={`toast toast-${toast.tone}`} role="status">
          <Icon name={toast.tone === "error" ? "warning" : "check"} size={18} />
          {toast.message}
        </div>
      ) : null}
    </div>
  );
}
